import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 hover:bg-white/80"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.backToHome')}
        </Button>

        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-t-2xl">
            <h1 className="text-4xl font-bold mb-4">{t('legal.privacyPolicy.title')}</h1>
            <p className="text-lg opacity-90">
              {t('legal.privacyPolicy.lastUpdated')}
            </p>
          </div>

          <div className="bg-white p-8 rounded-b-2xl shadow-lg">
            <div className="prose max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.privacyPolicy.introduction.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.privacyPolicy.introduction.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.privacyPolicy.informationWeCollect.title')}
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      {t('legal.privacyPolicy.informationWeCollect.personalInfo.title')}
                    </h3>
                    <ul className="list-disc pl-6 space-y-1 text-gray-600">
                      {(t('legal.privacyPolicy.informationWeCollect.personalInfo.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      {t('legal.privacyPolicy.informationWeCollect.automaticInfo.title')}
                    </h3>
                    <ul className="list-disc pl-6 space-y-1 text-gray-600">
                      {(t('legal.privacyPolicy.informationWeCollect.automaticInfo.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.privacyPolicy.howWeUse.title')}
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  {(t('legal.privacyPolicy.howWeUse.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.privacyPolicy.informationSharing.title')}
                </h2>
                <p className="text-gray-600 mb-4">
                  {t('legal.privacyPolicy.informationSharing.description')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  {(t('legal.privacyPolicy.informationSharing.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.privacyPolicy.dataSecurity.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.privacyPolicy.dataSecurity.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.privacyPolicy.yourRights.title')}
                </h2>
                <p className="text-gray-600 mb-4">
                  {t('legal.privacyPolicy.yourRights.description')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  {(t('legal.privacyPolicy.yourRights.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.privacyPolicy.cookiesTracking.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.privacyPolicy.cookiesTracking.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.privacyPolicy.childrenPrivacy.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.privacyPolicy.childrenPrivacy.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.privacyPolicy.internationalTransfers.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.privacyPolicy.internationalTransfers.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.privacyPolicy.policyChanges.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.privacyPolicy.policyChanges.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.privacyPolicy.contactUs.title')}
                </h2>
                <div className="text-gray-600 space-y-2">
                  <p>{t('legal.privacyPolicy.contactUs.description')}</p>
                  <p>{t('legal.privacyPolicy.contactUs.email')}</p>
                  <p>{t('legal.privacyPolicy.contactUs.address')}</p>
                  <p>{t('legal.privacyPolicy.contactUs.phone')}</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
